/**
 * Outreach Campaigns page – list, create, edit.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, Select, Switch, message, Card, Tooltip, Typography, Divider, Popconfirm, Tabs,
} from 'antd';
import { PlusOutlined, EditOutlined, CopyOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Campaign, CampaignCreate, CampaignUpdate } from '../types';
import { outreachCampaignsApi } from '../api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

/** Generate a unique campaign ID like "camp_a1b2c3d4" */
const generateCampaignId = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = 'camp_';
  for (let i = 0; i < 8; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
};

const UNSUBSCRIBE_HTML = '<p style="color:#888;font-size:12px;margin-top:24px;">To unsubscribe from future communications, please reply to this email with "UNSUBSCRIBE" in the subject line.</p>';

const { Text } = Typography;

/** Check if quill HTML content is effectively empty */
const isQuillEmpty = (html: string) => {
  if (!html) return true;
  const stripped = html.replace(/<(.|\n)*?>/g, '').trim();
  return stripped.length === 0;
};

/** Quill toolbar configuration with all standard rich text formatting options */
const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['blockquote'],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,  // preserves pasted formatting properly
  },
};

const QUILL_FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'align',
  'list',
  'indent',
  'blockquote',
  'link',
];

const QUILL_TOOLBAR_TITLES: Array<{ selector: string; title: string }> = [
  { selector: '.ql-header', title: 'Heading Style' },
  { selector: '.ql-bold', title: 'Bold' },
  { selector: '.ql-italic', title: 'Italic' },
  { selector: '.ql-underline', title: 'Underline' },
  { selector: '.ql-strike', title: 'Strikethrough' },
  { selector: '.ql-color', title: 'Text Color' },
  { selector: '.ql-background', title: 'Highlight Color' },
  { selector: '.ql-align', title: 'Text Alignment' },
  { selector: '.ql-list[value="ordered"]', title: 'Numbered List' },
  { selector: '.ql-list[value="bullet"]', title: 'Bulleted List' },
  { selector: '.ql-indent[value="-1"]', title: 'Decrease Indent' },
  { selector: '.ql-indent[value="+1"]', title: 'Increase Indent' },
  { selector: '.ql-blockquote', title: 'Block Quote' },
  { selector: '.ql-link', title: 'Insert Link' },
  { selector: '.ql-clean', title: 'Clear Formatting' },
];

type CampaignTab = 'active' | 'deleted';

export const OutreachCampaigns: React.FC = () => {
  const [activeTab, setActiveTab] = useState<CampaignTab>('active');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [form] = Form.useForm();
  const [bodyValue, setBodyValue] = useState('');
  const [subjectValue, setSubjectValue] = useState('');

  useEffect(() => { fetchCampaigns(); }, [page, pageSize, activeTab]);
  useEffect(() => {
    if (!modalVisible) return;
    // Quill toolbar is rendered after mount; defer so we can set hover titles.
    const timer = window.setTimeout(() => {
      const toolbar = document.querySelector('.campaign-email-editor .ql-toolbar');
      if (!toolbar) return;
      QUILL_TOOLBAR_TITLES.forEach(({ selector, title }) => {
        toolbar.querySelectorAll(selector).forEach((el) => {
          el.setAttribute('title', title);
        });
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [modalVisible]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await outreachCampaignsApi.list({
        page, page_size: pageSize, deleted: activeTab === 'deleted',
      });
      setCampaigns(res.items); setTotal(res.total);
    } catch { message.error('Failed to fetch campaigns'); }
    finally { setLoading(false); }
  };

  const handleCreate = () => {
    setEditingCampaign(null); form.resetFields();
    form.setFieldsValue({ is_active: true, brand: 'sns_intel', campaign_id: generateCampaignId(), body: '' });
    setBodyValue('');
    setSubjectValue('');
    setModalVisible(true);
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    form.setFieldsValue({
      name: campaign.name, campaign_id: campaign.campaign_id, brand: campaign.brand,
      subject: campaign.subject, body: campaign.body, is_active: campaign.is_active,
    });
    setBodyValue(campaign.body || '');
    setSubjectValue(campaign.subject || '');
    setModalVisible(true);
  };

  const handleBodyChange = useCallback((content: string) => {
    setBodyValue(content);
    form.setFieldsValue({ body: content });
  }, [form]);

  const handleSubmit = async (values: any) => {
    try {
      // Use bodyValue directly since ReactQuill updates it
      const body = bodyValue;
      if (editingCampaign) {
        const update: CampaignUpdate = {
          name: values.name, subject: values.subject, body,
          is_active: values.is_active, brand: values.brand,
        };
        await outreachCampaignsApi.update(editingCampaign.id, update);
        message.success('Campaign updated');
      } else {
        const create: CampaignCreate = {
          campaign_id: values.campaign_id, brand: values.brand, name: values.name,
          subject: values.subject, body, is_active: values.is_active,
        };
        await outreachCampaignsApi.create(create);
        message.success('Campaign created');
      }
      setModalVisible(false); fetchCampaigns();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const handleDeleteCampaign = async (campaign: Campaign) => {
    try {
      await outreachCampaignsApi.delete(campaign.id);
      message.success(`Campaign "${campaign.name}" deleted`);
      fetchCampaigns();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  const statusColorMap: Record<string, string> = {
    draft: 'default',
    active: 'green',
    running: 'blue',
    scheduled: 'cyan',
    paused: 'orange',
    stopped: 'red',
    completed: 'purple',
    cancelled: 'default',
    deleted: 'red',
  };

  const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  const baseColumns: ColumnsType<Campaign> = [
    { title: 'Campaign ID', dataIndex: 'campaign_id', key: 'campaign_id', width: 160 },
    { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: 'Brand', dataIndex: 'brand', key: 'brand', width: 120,
      render: (b: string) => <Tag color={b === 'sns_intel' ? 'blue' : 'purple'}>{b}</Tag> },
    {
      title: 'Status', key: 'status', width: 120,
      render: (_, r) => {
        const displayStatus = r.status === 'draft' && r.is_active ? 'active' : r.status;
        return <Tag color={statusColorMap[displayStatus] || 'default'}>{displayStatus.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Sent', dataIndex: 'total_sent', key: 'total_sent', width: 70,
      render: (v: number) => v || 0,
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Bounced</span>, dataIndex: 'total_bounced', key: 'total_bounced', width: 100,
      render: (v: number) => v > 0 ? <Text type="danger">{v}</Text> : 0,
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Unsubscribed</span>, dataIndex: 'total_unsubscribed', key: 'total_unsubscribed', width: 110,
      render: (v: number) => v > 0 ? <Text type="warning">{v}</Text> : 0,
    },
  ];

  const activeColumns: ColumnsType<Campaign> = [
    ...baseColumns,
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_, r) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(r)} size="small">Edit</Button>
          <Popconfirm
            title="Delete this campaign?"
            description="The campaign will be removed from the active list and any pending scheduled sends will be cancelled. It will remain in the Deleted tab for auditing."
            onConfirm={() => handleDeleteCampaign(r)}
            okText="Delete"
            okType="danger"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const deletedColumns: ColumnsType<Campaign> = [
    ...baseColumns,
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Deleted At</span>,
      dataIndex: 'deleted_at', key: 'deleted_at', width: 180,
      render: (v: string | null) => <Text type="secondary">{formatDateTime(v)}</Text>,
    },
  ];

  const columns = activeTab === 'active' ? activeColumns : deletedColumns;

  const hasBody = !isQuillEmpty(bodyValue);

  const previewHtml = useMemo(() => {
    if (!hasBody) return '';
    return `<p>Hi John,</p>${bodyValue}<p>[Sender account footer will appear here]</p>${UNSUBSCRIBE_HTML}`;
  }, [bodyValue, hasBody]);

  return (
    <div>
      <style>{`
        /* Rich text editor styling for campaign email body */
        .campaign-email-editor .ql-container {
          min-height: 200px;
          font-size: 14px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }
        .campaign-email-editor .ql-editor {
          min-height: 200px;
          line-height: 1.6;
        }
        .campaign-email-editor .ql-editor.ql-blank::before {
          font-style: normal;
          color: #bfbfbf;
        }
        .campaign-email-editor .ql-toolbar {
          border-radius: 6px 6px 0 0;
          border-color: #d9d9d9;
          background: #fafafa;
        }
        .campaign-email-editor .ql-container {
          border-radius: 0 0 6px 6px;
          border-color: #d9d9d9;
        }
        /* Email preview styling - WYSIWYG match */
        .email-preview-body {
          padding: 16px;
          font-size: 14px;
          line-height: 1.6;
          background: #fff;
          color: #000;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }
        .email-preview-body p {
          margin: 0 0 10px 0;
        }
        .email-preview-body ul, .email-preview-body ol {
          margin: 0 0 10px 0;
          padding-left: 24px;
        }
        .email-preview-body blockquote {
          border-left: 4px solid #ccc;
          margin: 0 0 10px 0;
          padding-left: 16px;
          color: #666;
        }
        .email-preview-body a {
          color: #1890ff;
        }
        .email-preview-body h1, .email-preview-body h2, .email-preview-body h3 {
          margin: 0 0 10px 0;
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 18, fontWeight: 600 }}>Campaigns</span>
        {activeTab === 'active' && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>Create Campaign</Button>
        )}
      </div>
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
        bodyStyle={{ paddingTop: 0 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key as CampaignTab);
            setPage(1);
          }}
          items={[
            { key: 'active', label: 'Active' },
            { key: 'deleted', label: 'Deleted' },
          ]}
        />
        <Table columns={columns} dataSource={campaigns} loading={loading} rowKey="id"
          locale={{
            emptyText: activeTab === 'deleted'
              ? 'No deleted campaigns. Deleted campaigns appear here for auditing.'
              : 'No campaigns yet.',
          }}
          pagination={{ current: page, pageSize, total, showSizeChanger: true,
            showTotal: (t) => `Total ${t}`, onChange: (p, ps) => { setPage(p); setPageSize(ps); } }} />
      </Card>
      <Modal title={editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
        open={modalVisible} onCancel={() => setModalVisible(false)} footer={null} width={800}
        destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!editingCampaign && (
            <Form.Item name="campaign_id" label="Campaign ID" rules={[{ required: true }]}
              tooltip="Auto-generated unique identifier for this campaign">
              <Input readOnly
                addonAfter={
                  <Tooltip title="Generate new ID">
                    <CopyOutlined onClick={() => form.setFieldsValue({ campaign_id: generateCampaignId() })} style={{ cursor: 'pointer' }} />
                  </Tooltip>
                }
              />
            </Form.Item>
          )}
          <Form.Item name="name" label="Campaign Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="brand" label="Brand" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="sns_intel">SNS Intel</Select.Option>
              <Select.Option value="sns_research">SNS Research</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="subject" label="Email Subject" rules={[{ required: true }]}>
            <Input onChange={e => setSubjectValue(e.target.value)} />
          </Form.Item>
          <Form.Item
            name="body"
            label="Email Body"
            rules={[{
              required: true,
              validator: (_, value) => {
                if (isQuillEmpty(value) && isQuillEmpty(bodyValue)) {
                  return Promise.reject(new Error('Please enter the email body'));
                }
                return Promise.resolve();
              },
            }]}
            extra={
              <span style={{ color: '#888', fontSize: 12, display: 'block', marginTop: 8 }}>
                <strong>Note:</strong> A personalized greeting (e.g. "Hi John,") and the sender account's
                footer/signature are added automatically. Do <strong>not</strong> include them in the body.
                You select which sender account to use when sending.
              </span>
            }
          >
            <div className="campaign-email-editor">
              <ReactQuill
                theme="snow"
                value={bodyValue}
                onChange={handleBodyChange}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write your email body here. Do not include greeting or signature — they are added automatically."
                style={{ background: '#fff' }}
              />
            </div>
          </Form.Item>

          {hasBody && (
            <>
              <Divider style={{ margin: '8px 0 16px' }} />
              <div style={{ marginBottom: 16 }}>
                <Space style={{ marginBottom: 8 }}>
                  <EyeOutlined />
                  <Text strong>Email Preview</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>(sample with name "John")</Text>
                </Space>
                <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                  {subjectValue && (
                    <div style={{ padding: '10px 16px', background: '#fafafa', borderBottom: '1px solid #e8e8e8' }}>
                      <Text strong>Subject: </Text><Text>{subjectValue}</Text>
                    </div>
                  )}
                  <div
                    className="email-preview-body"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              </div>
            </>
          )}

          <Form.Item name="is_active" valuePropName="checked"><Switch checkedChildren="Active" unCheckedChildren="Inactive" /></Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">{editingCampaign ? 'Update' : 'Create'}</Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
